import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/ui/Seo";
import { HeroSlideshow } from "@/components/sections/HeroSlideshow";
import { SectorsBand } from "@/components/sections/SectorsBand";
import { Picture } from "@/components/ui/Picture";
import { LazyMap } from "@/components/ui/LazyMap";
import { businessConfig } from "@/config/business.config";
import { getLocalBusinessSchema } from "@/data/structuredData";
import { services } from "@/data/services";
import type { PhotoKey } from "@/data/photos";

const PHONE = businessConfig.contact.phoneDisplay;
const PHONE_HREF = businessConfig.contact.phoneHref;
const SMS_HREF = "sms:+61432068695";
const YEARS = businessConfig.people.principal.yearsOfIndustryExperience;

const trustFacts = [
  `${YEARS}+ years experience`,
  "Residential & commercial",
  "Competitive, upfront pricing",
  "Free quotes over the phone",
];

const team = [
  { name: "Salvatore Frazzetto", role: "Senior Operator & Director" },
  { name: "Tanner Frazzetto", role: "Operations & quotes" },
  { name: "Will Frazzetto", role: "Lead Operator" },
];

interface Featured {
  slug: string;
  num: string;
  name: string;
  desc: string;
  photoKey: PhotoKey;
  alt: string;
}
const featured: Featured[] = [
  { slug: "concrete-cutting", num: "01", name: "Concrete Cutting", desc: "Straight, accurate cuts in slabs, walls and structural concrete for openings and alterations.", photoKey: "saw-cutting", alt: "Diamond saw cutting precast concrete panels" },
  { slug: "core-drilling", num: "02", name: "Core Drilling", desc: "Clean, round holes for plumbing, electrical, HVAC and fixings — small conduits to large cores.", photoKey: "core-drilling", alt: "Core drilling through a concrete wall" },
  { slug: "floor-and-slab-sawing", num: "03", name: "Floor & Slab Sawing", desc: "Track and floor sawing for new openings, doorways and controlled slab sections.", photoKey: "wall-opening", alt: "Large opening cut into a precast concrete wall" },
  { slug: "concrete-removal", num: "04", name: "Concrete Removal", desc: "Breaking out and removing cut sections, leaving the area ready for the next stage.", photoKey: "concrete-removal", alt: "Cut concrete broken out and removed on site" },
];

const moreServices = [
  "road-sawing",
  "hand-sawing",
  "expansion-joint-cutting",
  "drainage-and-plumbing-penetrations",
  "wall-and-doorway-openings",
];

const process = [
  { num: "1", title: "Get in touch", desc: "Call our team or request a callback and tell us about the job." },
  { num: "2", title: "Send photos & sizes", desc: "A couple of photos and rough measurements are usually all we need to price it." },
  { num: "3", title: "We cut it", desc: "We arrive on time with the right equipment and complete the work to spec." },
];

interface GalleryItem {
  photoKey: PhotoKey;
  alt: string;
  label: string;
  span: number;
  row: number;
}
const gallery: GalleryItem[] = [
  { photoKey: "road-sawing", alt: "Road saw cutting a concrete culvert on a Melbourne civil site", label: "Road sawing", span: 2, row: 2 },
  { photoKey: "commercial-site", alt: "Concrete cutting on a commercial construction site", label: "Commercial site", span: 2, row: 1 },
  { photoKey: "wall-opening", alt: "Large opening cut into a precast concrete wall", label: "Wall opening", span: 1, row: 1 },
  { photoKey: "concrete-removal", alt: "Cut concrete being broken out and removed", label: "Removal", span: 1, row: 1 },
  { photoKey: "core-drilling", alt: "Operator core drilling through a concrete wall", label: "Core drilling", span: 2, row: 1 },
  { photoKey: "slab-cut-line", alt: "Clean control joint cut into a concrete slab", label: "Slab sawing", span: 2, row: 1 },
];

const phones = [
  { label: "Quotes & enquiries · Salvatore", number: PHONE, href: PHONE_HREF },
];


export function Home() {
  return (
    <>
      <Seo
        title="Concrete Cutting & Core Drilling in Melbourne"
        description={businessConfig.seo.defaultDescription}
        path="/"
        structuredData={getLocalBusinessSchema()}
      />

      {/* ============ HERO ============ */}
      <section className="hm-hero">
        <HeroSlideshow />
        <div className="hm-hero__overlay" aria-hidden="true" />
        <div className="container hm-hero__inner">
          <div className="hm-hero__content">
            <div className="eyebrow eyebrow--hero anim-rise">Melbourne · South-East · Victoria-wide</div>
            <h1 className="hm-hero__title anim-rise anim-rise-1">
              Precision concrete cutting &amp; <span className="accent">core drilling</span>
            </h1>
            <p className="hm-hero__lede anim-rise anim-rise-2">
              Frazzetto Concrete Cutting &amp; Coring handles everything from small home jobs to major
              commercial works across Melbourne — clean, accurate cutting at a fair price, backed by{" "}
              {YEARS} years in the trade. Send us a photo and get a quick quote over the phone.
            </p>
            <div className="hm-hero__actions anim-rise anim-rise-3">
              <Link to="/contact" className="btn btn--primary btn--lg">
                Get a Free Quote →
              </Link>
              <a href={PHONE_HREF} className="hm-hero__call">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1l-2.2 2.2Z" />
                </svg>
                <span className="hm-hero__call-text">
                  <span className="hm-hero__call-label">Call now</span>
                  <span className="hm-hero__call-number">{PHONE}</span>
                </span>
              </a>
            </div>
            <p className="hm-hero__note anim-rise anim-rise-4">
              Send us photos, measurements &amp; the job location for a fast quote.
            </p>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="hm-trust" aria-label="Why choose Frazzetto">
        <div className="container">
          <ul className="hm-trust__list">
            {trustFacts.map((f) => (
              <li key={f}>
                <span className="hm-trust__tick" aria-hidden="true">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ ABOUT TEASER ============ */}
      <section id="about" className="hm-about">
        <div className="container hm-about__grid">
          <div>
            <div className="eyebrow">About Frazzetto</div>
            <h2 className="hm-h2">A Melbourne family name in concrete cutting</h2>
            <p className="hm-p">
              Frazzetto Concrete Cutting &amp; Coring handles residential, commercial and construction work
              across Melbourne. From single core holes to complete cut-and-remove jobs, we deliver accurate
              work, straightforward pricing and more than {YEARS} years of hands-on industry experience.
            </p>
            <p className="hm-p">
              We're known for accurate cuts, straightforward pricing and reliable service — arriving on time,
              working to spec, and leaving the site ready for the next trade.
            </p>
            <div className="hm-team">
              {team.map((t) => (
                <div key={t.name} className="hm-team__cell">
                  <div className="hm-team__name">{t.name}</div>
                  <div className="hm-team__role">{t.role}</div>
                </div>
              ))}
            </div>
            <p className="hm-fineprint">
              * {YEARS}+ years refers to Salvatore's experience in the industry, not the age of the business.
            </p>
          </div>
          <div className="hm-about__media">
            <div className="hm-about__img">
              <Picture photoKey="core-drilling" alt="Frazzetto operator core drilling a large penetration through a concrete wall" sizes="(max-width: 980px) 100vw, 45vw" />
            </div>
            <div className="hm-about__tag">Core drilling · South-East Melbourne</div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section id="services" className="hm-services">
        <div className="container">
          <div className="hm-head">
            <div>
              <div className="eyebrow">Our services</div>
              <h2 className="hm-h2">Concrete cutting &amp; coring</h2>
            </div>
            <p className="hm-head__intro">
              From a single core hole to full-site cutting — for homeowners, trades, builders and commercial
              sites. Fair, upfront pricing on every job.
            </p>
          </div>
          <div className="hm-svc-grid">
            {featured.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="hm-svc">
                <div className="hm-svc__media">
                  <Picture photoKey={s.photoKey} alt={s.alt} sizes="(max-width: 600px) 100vw, (max-width: 980px) 50vw, 25vw" />
                  <span className="hm-svc__num">{s.num}</span>
                </div>
                <h3 className="hm-svc__name">{s.name}</h3>
                <p className="hm-svc__desc">{s.desc}</p>
              </Link>
            ))}
          </div>
          <div className="hm-more">
            <span className="hm-more__label">Also available:</span>
            {moreServices.map((slug) => {
              const svc = services.find((x) => x.slug === slug);
              if (!svc) return null;
              return (
                <Link key={slug} to={`/services/${slug}`} className="hm-more__link">
                  <span className="hm-diamond" aria-hidden="true" />
                  {svc.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SECTORS (kept icon medallions) ============ */}
      <SectorsBand />

      {/* ============ PROCESS ============ */}
      <section id="process" className="hm-process">
        <div className="container">
          <div className="hm-process__head">
            <div className="eyebrow eyebrow--hero">Getting a quote</div>
            <h2 className="hm-h2 hm-h2--light">Send a photo. Get a quote.</h2>
            <p className="hm-p hm-p--light">
              Most jobs we can quote directly over the phone or from a couple of photos — no waiting days. For
              larger or more complex works, we'll arrange a site visit.
            </p>
          </div>
          <div className="hm-steps">
            {process.map((p) => (
              <div key={p.num} className="hm-step">
                <div className="hm-step__num">{p.num}</div>
                <h3 className="hm-step__title">{p.title}</h3>
                <p className="hm-step__desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THREE WAYS TO QUOTE ============ */}
      <section className="hm-ways">
        <div className="container">
          <div className="hm-ways__head">
            <div className="hm-ways__eyebrow">Send a photo. Get a quote.</div>
            <h2 className="hm-ways__title">Three easy ways to get a price</h2>
          </div>
          <div className="hm-ways__grid">
            <Link to="/contact" className="hm-way">
              <span className="hm-way__num">1</span>
              <span><strong>Fill in the form</strong>Send your job details and photos online.</span>
            </Link>
            <a href={SMS_HREF} className="hm-way">
              <span className="hm-way__num">2</span>
              <span><strong>Text or email photos</strong>Text {PHONE} or email {businessConfig.contact.email} directly.</span>
            </a>
            <a href={PHONE_HREF} className="hm-way">
              <span className="hm-way__num">3</span>
              <span><strong>Call for a quote</strong>Talk it through and get a price on the spot.</span>
            </a>
          </div>
        </div>
      </section>

      {/* ============ WORK GALLERY ============ */}
      <section id="work" className="hm-work">
        <div className="container">
          <div className="hm-head">
            <div>
              <div className="eyebrow">Recent work</div>
              <h2 className="hm-h2">Work across Melbourne</h2>
            </div>
            <p className="hm-head__intro">
              Cutting, coring and removal on residential and commercial projects throughout Melbourne and
              Victoria.
            </p>
          </div>
          <div className="hm-gallery">
            {gallery.map((g, i) => (
              <figure key={i} className="hm-gallery__item" style={{ gridColumn: `span ${g.span}`, gridRow: `span ${g.row}` }}>
                <Picture photoKey={g.photoKey} alt={g.alt} sizes="(max-width: 600px) 50vw, 25vw" />
                <figcaption>{g.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      <section id="reviews" className="hm-reviews">
        <div className="container hm-reviews__inner">
          <div className="eyebrow eyebrow--center">Reviews</div>
          <h2 className="hm-h2">What our customers say</h2>
          <div className="hm-reviews__card">
            <div className="hm-reviews__badge">
              <span className="hm-reviews__g">G</span>
              <span className="hm-reviews__glabel">
                Reviews<br />
                <span>on Google</span>
              </span>
            </div>
            <p className="hm-reviews__text">
              We let the work and our customers do the talking. Reviews from real Frazzetto jobs appear here —
              see what people say on Google, or leave us a review once your job's done.
            </p>
            <div className="hm-reviews__actions">
              <a href={businessConfig.online.googleBusinessProfileUrl} target="_blank" rel="noopener" className="btn btn--primary">
                Read us on Google →
              </a>
              <a href={businessConfig.online.googleBusinessProfileUrl} target="_blank" rel="noopener" className="btn btn--secondary">
                Leave a review
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICE AREAS TEASER ============ */}
      <section id="areas" className="hm-areas">
        <div className="container hm-areas__grid">
          <div>
            <div className="eyebrow">Service Areas</div>
            <h2 className="hm-h2">Right across Melbourne &amp; Victoria</h2>
            <p className="hm-p">
              We cover Melbourne, the South-East and Greater Geelong, and travel well beyond — up to around two
              hours across central and southern Victoria for the right job. Not sure if we reach you? Give us a
              call.
            </p>
            <div className="hm-areas__actions">
              <Link to="/service-areas" className="btn btn--secondary">See all service areas →</Link>
              <a href={PHONE_HREF} className="btn btn--secondary">☎ {PHONE}</a>
            </div>
          </div>
          <div className="hm-areas__map">
            <LazyMap title="Frazzetto service area — Melbourne & Victoria" height={380} />
          </div>
        </div>
      </section>

      {/* ============ CALLBACK CTA ============ */}
      <CallbackCta />

      <style>{`
        /* ---- Hero ---- */
        .hm-hero { position: relative; background: var(--graphite); overflow: hidden; }
        .hm-hero__overlay {
          position: absolute; inset: 0; z-index: 1;
          background: linear-gradient(100deg, rgba(24,23,21,0.94) 0%, rgba(24,23,21,0.86) 38%, rgba(24,23,21,0.5) 70%, rgba(24,23,21,0.28) 100%);
        }
        .hm-hero__inner {
          position: relative; z-index: 2;
          padding-block: clamp(56px, 9vw, 120px);
          min-height: clamp(500px, 74vh, 680px);
          display: flex; align-items: center;
        }
        .hm-hero__content { max-width: 640px; color: #fff; }
        .eyebrow--hero { color: var(--orange-hero); }
        .hm-hero__title {
          font-family: var(--font-display); font-weight: 900; text-transform: uppercase;
          font-size: clamp(2.5rem, 6vw, 4.8rem); line-height: 0.95; letter-spacing: -0.015em;
          margin-top: 18px; text-wrap: balance; color: #fff;
        }
        .hm-hero__lede { font-size: clamp(1.08rem, 1.6vw, 1.28rem); line-height: 1.55; color: #e6e1d9; max-width: 52ch; margin-top: 22px; }
        .hm-hero__actions { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; margin-top: 30px; }
        .hm-hero__call { display: inline-flex; align-items: center; gap: 11px; color: #fff; border: 2px solid rgba(255,255,255,0.55); padding: 12px 22px; text-decoration: none; transition: background var(--duration-fast), color var(--duration-fast); }
        .hm-hero__call:hover { background: #fff; color: var(--graphite); }
        .hm-hero__call-text { display: flex; flex-direction: column; line-height: 1.05; }
        .hm-hero__call-label { font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0.85; }
        .hm-hero__call-number { font-family: var(--font-display); font-size: 19px; font-weight: 900; }
        .hm-hero__note { font-family: var(--font-mono); font-size: 12.5px; line-height: 1.5; color: #c9c4bb; margin-top: 16px; }

        /* ---- Shared headings ---- */
        .hm-h2 { font-family: var(--font-display); font-weight: 900; text-transform: uppercase; font-size: clamp(2rem, 3.6vw, 3.1rem); line-height: 1.02; color: var(--graphite); margin-top: 14px; text-wrap: balance; }
        .hm-h2--light { color: #fff; }
        .hm-p { font-size: 1.14rem; line-height: 1.65; color: var(--body-text-soft); margin-top: 18px; }
        .hm-p--light { color: var(--muted-on-dark); }
        .hm-fineprint { font-family: var(--font-mono); font-size: 11px; line-height: 1.6; color: #8a857d; margin-top: 16px; }

        /* ---- Trust strip ---- */
        .hm-trust { background: var(--graphite); color: #fff; border-top: 1px solid rgba(255,255,255,0.08); }
        .hm-trust__list { display: flex; flex-wrap: wrap; }
        .hm-trust__list li { flex: 1 1 220px; padding: 20px 22px; border-right: 1px solid rgba(255,255,255,0.1); display: flex; align-items: center; gap: 12px; }
        .hm-trust__tick { color: var(--orange); font-family: var(--font-display); font-weight: 900; font-size: 20px; }
        .hm-trust__list li span:last-child { font-family: var(--font-display); font-weight: 700; font-size: 1.02rem; text-transform: uppercase; line-height: 1.15; }

        /* ---- About ---- */
        .hm-about { background: var(--page); }
        .hm-about, .hm-services, .hm-work, .hm-reviews, .hm-areas { padding-block: var(--section-pad); }
        .hm-about__grid { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: clamp(32px, 5vw, 68px); align-items: center; }
        .hm-team { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border-strong); border: 1px solid var(--border-strong); margin-top: 30px; }
        .hm-team__cell { background: var(--page); padding: 22px 20px; }
        .hm-team__name { font-family: var(--font-display); font-weight: 900; text-transform: uppercase; font-size: 1.14rem; color: var(--graphite); line-height: 1; }
        .hm-team__role { font-family: var(--font-mono); font-size: 11px; text-transform: uppercase; color: var(--orange-hover); margin-top: 9px; }
        .hm-about__media { position: relative; min-width: 0; }
        .hm-about__img { aspect-ratio: 4/5; overflow: hidden; }
        .hm-about__tag { position: absolute; left: 0; bottom: 0; background: var(--orange); color: #fff; font-family: var(--font-display); font-weight: 800; font-size: 13px; text-transform: uppercase; padding: 12px 18px; }

        /* ---- Services ---- */
        .hm-services { background: var(--white); border-top: 1px solid var(--border-strong); border-bottom: 1px solid var(--border-strong); }
        .hm-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; flex-wrap: wrap; }
        .hm-head__intro { max-width: 40ch; font-size: 1.08rem; line-height: 1.6; color: var(--body-text-soft); }
        .hm-svc-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 40px; }
        .hm-svc { display: block; text-decoration: none; }
        .hm-svc__media { position: relative; aspect-ratio: 4/3; overflow: hidden; }
        .hm-svc__media img { transition: transform 0.5s var(--ease-out); }
        .hm-svc:hover .hm-svc__media img { transform: scale(1.06); }
        .hm-svc__num { position: absolute; left: 0; top: 0; background: var(--orange); color: #fff; font-family: var(--font-mono); font-size: 11px; font-weight: 700; padding: 5px 9px; }
        .hm-svc__name { font-family: var(--font-display); font-weight: 800; text-transform: uppercase; font-size: 1.16rem; line-height: 1.08; color: var(--graphite); margin-top: 14px; }
        .hm-svc__desc { font-size: 0.96rem; line-height: 1.5; color: var(--muted-text); margin-top: 8px; }
        .hm-more { margin-top: 40px; padding-top: 28px; border-top: 1px solid var(--border-subtle); display: flex; flex-wrap: wrap; align-items: center; gap: 14px 28px; }
        .hm-more__label { font-family: var(--font-display); font-weight: 800; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--graphite); }
        .hm-more__link { display: inline-flex; align-items: center; gap: 8px; font-size: 1rem; color: var(--graphite); border-bottom: 2px solid var(--orange); padding-bottom: 2px; text-decoration: none; }
        .hm-more__link:hover { color: var(--orange); }
        .hm-diamond { width: 7px; height: 7px; background: var(--orange); transform: rotate(45deg); display: inline-block; }

        /* ---- Process ---- */
        .hm-process { background: var(--graphite); color: #fff; padding-block: var(--section-pad); }
        .hm-process__head { max-width: 660px; }
        .hm-process__head .eyebrow { color: var(--orange); }
        .hm-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.12); margin-top: 40px; }
        .hm-step { background: var(--graphite); padding: 28px 24px; }
        .hm-step__num { font-family: var(--font-display); font-weight: 900; font-size: 2.4rem; color: var(--orange); line-height: 1; }
        .hm-step__title { font-family: var(--font-display); font-weight: 800; text-transform: uppercase; font-size: 1.14rem; line-height: 1.12; margin-top: 14px; color: #fff; }
        .hm-step__desc { font-size: 0.98rem; line-height: 1.55; color: #aea89f; margin-top: 10px; }

        /* ---- Three ways ---- */
        .hm-ways { background: var(--graphite-panel); color: #fff; padding-block: var(--section-pad-sm); }
        .hm-ways__head { text-align: center; margin-bottom: 28px; }
        .hm-ways__eyebrow { font-family: var(--font-mono); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--orange-hero); }
        .hm-ways__title { font-family: var(--font-display); font-weight: 900; text-transform: uppercase; font-size: clamp(1.6rem, 2.8vw, 2.4rem); margin-top: 10px; color: #fff; }
        .hm-ways__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 14px; }
        .hm-way { display: flex; align-items: flex-start; gap: 14px; background: var(--graphite); border: 1px solid rgba(255,255,255,0.12); padding: 24px; text-decoration: none; transition: border-color var(--duration-fast); }
        .hm-way:hover { border-color: var(--orange); }
        .hm-way__num { font-family: var(--font-display); font-weight: 900; font-size: 1.6rem; color: var(--orange); line-height: 1; }
        .hm-way span span, .hm-way > span { font-size: 0.98rem; line-height: 1.5; color: #dce2e8; min-width: 0; overflow-wrap: anywhere; }
        .hm-way strong { font-family: var(--font-display); text-transform: uppercase; color: #fff; font-size: 1.05rem; display: block; margin-bottom: 5px; }

        /* ---- Work gallery ---- */
        .hm-work { background: var(--band); }
        .hm-gallery { display: grid; grid-template-columns: repeat(4, 1fr); grid-auto-rows: 200px; gap: 12px; margin-top: 40px; }
        .hm-gallery__item { margin: 0; position: relative; overflow: hidden; }
        .hm-gallery__item img { transition: transform 0.5s var(--ease-out); }
        .hm-gallery__item:hover img { transform: scale(1.05); }
        .hm-gallery__item figcaption { position: absolute; left: 12px; bottom: 12px; font-family: var(--font-mono); font-size: 11px; text-transform: uppercase; color: #fff; text-shadow: 0 1px 6px rgba(0,0,0,0.7); }

        /* ---- Reviews ---- */
        .hm-reviews { background: var(--page); border-top: 1px solid var(--border-strong); }
        .hm-reviews__inner { max-width: 860px; text-align: center; }
        .hm-reviews__inner .eyebrow { justify-content: center; }
        .hm-reviews__card { background: var(--white); border: 1px solid var(--border-strong); padding: clamp(28px, 4vw, 44px); margin-top: 34px; }
        .hm-reviews__badge { display: inline-flex; align-items: center; justify-content: center; gap: 12px; }
        .hm-reviews__g { width: 44px; height: 44px; border-radius: 50%; background: var(--orange); display: flex; align-items: center; justify-content: center; color: #fff; font-family: var(--font-display); font-weight: 900; font-size: 22px; }
        .hm-reviews__glabel { font-family: var(--font-display); font-weight: 800; text-transform: uppercase; font-size: 1.2rem; color: var(--graphite); text-align: left; line-height: 1.1; }
        .hm-reviews__glabel span { font-family: var(--font-mono); font-weight: 400; font-size: 11.5px; color: var(--muted-text); text-transform: none; }
        .hm-reviews__text { font-size: 1.12rem; line-height: 1.65; color: var(--body-text-soft); max-width: 52ch; margin: 22px auto 0; }
        .hm-reviews__actions { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; margin-top: 26px; }

        /* ---- Areas teaser ---- */
        .hm-areas { background: var(--white); border-top: 1px solid var(--border-strong); border-bottom: 1px solid var(--border-strong); }
        .hm-areas__grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(30px, 5vw, 60px); align-items: center; }
        .hm-areas__actions { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 24px; }
        .hm-areas__map { border: 1px solid var(--border-strong); box-shadow: var(--shadow-md); min-height: 380px; }

        @media (max-width: 980px) {
          .hm-about__grid, .hm-areas__grid { grid-template-columns: 1fr; }
          .hm-svc-grid { grid-template-columns: 1fr 1fr; }
          .hm-steps { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .hm-svc-grid { grid-template-columns: 1fr; }
          .hm-team { grid-template-columns: 1fr; }
          .hm-gallery { grid-template-columns: 1fr 1fr; }
          .hm-gallery__item { grid-column: span 1 !important; grid-row: span 1 !important; }
          /* Stack "Also available" as the label on its own line, then each link. */
          .hm-more { flex-direction: column; align-items: flex-start; gap: 14px; }
        }
      `}</style>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Homepage callback form — light-touch quote request (name/phone/suburb/     */
/* service). Posts via Web3Forms when configured, else opens the visitor's    */
/* email client. The full conditional quote form lives on /contact.          */
/* -------------------------------------------------------------------------- */
function CallbackCta() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", suburb: "", service: "" });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    const summary = `Name: ${form.name}\nPhone: ${form.phone}\nSuburb: ${form.suburb}\nService: ${form.service}`;
    try {
      // Netlify Forms (see the hidden "callback" form in index.html).
      const fd = new FormData();
      fd.append("form-name", "callback");
      fd.append("bot-field", "");
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("suburb", form.suburb);
      fd.append("service", form.service);
      fd.append("message", summary);
      await fetch("/", { method: "POST", body: fd });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="callback" className="hm-cta">
      <div className="container hm-cta__grid">
        <div>
          <div className="eyebrow" style={{ color: "var(--orange)" }}>Request a quote</div>
          <h2 className="hm-cta__title">Get a free quote today</h2>
          <p className="hm-cta__lede">
            Call our team direct for a quote over the phone, or leave your details and a photo of the job —
            we'll get back to you, usually the same day.
          </p>
          <div className="hm-cta__phones">
            {phones.map((ph) => (
              <a key={ph.href} href={ph.href} className="hm-cta__phone">
                <span className="hm-cta__phone-label">{ph.label}</span>
                <span className="hm-cta__phone-number"><span aria-hidden="true">☎</span> {ph.number}</span>
              </a>
            ))}
          </div>
          <div className="hm-cta__meta">
            <span>{businessConfig.hours.display}</span>
            <span>Fully insured</span>
            <span>Melbourne &amp; Victoria</span>
          </div>
        </div>

        <div className="hm-cta__formwrap">
          {submitted ? (
            <div className="hm-cta__success" role="status">
              <div className="hm-cta__check" aria-hidden="true"><span>✓</span></div>
              <h3>Thanks — we'll be in touch</h3>
              <p>
                We'll get back to you with a quote, usually the same day. Need it sorted now? Call {PHONE}.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="hm-cta__form"
              name="callback"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="callback" />
              <p hidden>
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <h3>Request a quote</h3>
              <p className="hm-cta__form-sub">Leave your details and we'll get straight back to you with a quote.</p>
              <div className="hm-cta__row">
                <label>
                  <span>Name *</span>
                  <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>
                <label>
                  <span>Phone *</span>
                  <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </label>
              </div>
              <label className="hm-cta__full">
                <span>Suburb</span>
                <input type="text" placeholder="e.g. Cranbourne" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} />
              </label>
              <label className="hm-cta__full">
                <span>What do you need done?</span>
                <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s.slug} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </label>
              <button type="submit" className="hm-cta__submit" disabled={sending}>
                {sending ? "Sending…" : "Request a quote →"}
              </button>
              <p className="hm-cta__disclaimer">No obligation. We only use your details to quote your job.</p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .hm-cta { background: var(--graphite-deep); color: #fff; padding-block: clamp(52px, 6vw, 100px); }
        .hm-cta__grid { display: grid; grid-template-columns: 1fr 0.9fr; gap: clamp(34px, 5vw, 68px); align-items: center; }
        .hm-cta__title { font-family: var(--font-display); font-weight: 900; text-transform: uppercase; font-size: clamp(2.1rem, 4vw, 3.5rem); line-height: 1; margin-top: 14px; text-wrap: balance; color: #fff; }
        .hm-cta__lede { font-size: 1.12rem; line-height: 1.65; color: var(--muted-on-dark); margin-top: 18px; max-width: 44ch; }
        .hm-cta__phones { display: grid; grid-template-columns: 1fr; gap: 14px; margin-top: 26px; }
        .hm-cta__phone { background: var(--graphite-panel); border: 1px solid rgba(255,255,255,0.12); padding: 16px 18px; text-decoration: none; transition: border-color var(--duration-fast); }
        .hm-cta__phone:hover { border-color: var(--orange); }
        .hm-cta__phone-label { font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--footer-muted); display: block; }
        .hm-cta__phone-number { display: flex; align-items: center; gap: 9px; font-family: var(--font-display); font-weight: 900; font-size: 1.4rem; color: #fff; margin-top: 6px; }
        .hm-cta__phone-number span { color: var(--orange); }
        .hm-cta__meta { margin-top: 22px; display: flex; flex-wrap: wrap; gap: 12px 26px; font-family: var(--font-mono); font-size: 12px; color: #aea89f; }

        .hm-cta__formwrap { background: var(--graphite-panel); border: 1px solid rgba(255,255,255,0.12); padding: clamp(24px, 3vw, 34px); }
        .hm-cta__form h3, .hm-cta__success h3 { font-family: var(--font-display); font-weight: 800; text-transform: uppercase; font-size: 1.35rem; color: #fff; }
        .hm-cta__form-sub { font-size: 0.95rem; color: #aea89f; margin-top: 8px; line-height: 1.5; }
        .hm-cta__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 20px; }
        .hm-cta__form label { display: block; }
        .hm-cta__full { margin-top: 12px; }
        .hm-cta__form label span { display: block; font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--footer-muted); margin-bottom: 7px; }
        .hm-cta__form input, .hm-cta__form select { width: 100%; background: var(--graphite); border: 1px solid rgba(255,255,255,0.16); color: #fff; padding: 12px 13px; font-size: 15px; font-family: inherit; }
        .hm-cta__submit { width: 100%; margin-top: 18px; background: var(--orange); color: #fff; border: none; font-family: var(--font-display); font-weight: 900; font-size: 16px; letter-spacing: 0.03em; text-transform: uppercase; padding: 16px; cursor: pointer; transition: background var(--duration-fast); }
        .hm-cta__submit:hover { background: var(--orange-hover); }
        .hm-cta__submit:disabled { opacity: 0.7; cursor: default; }
        .hm-cta__disclaimer { font-family: var(--font-mono); font-size: 10.5px; color: #726c63; margin-top: 11px; text-align: center; }

        .hm-cta__success { text-align: center; padding: 36px 10px; }
        .hm-cta__check { width: 54px; height: 54px; margin: 0 auto; background: var(--orange); display: flex; align-items: center; justify-content: center; transform: rotate(45deg); }
        .hm-cta__check span { transform: rotate(-45deg); color: #fff; font-size: 26px; }
        .hm-cta__success h3 { margin-top: 18px; }
        .hm-cta__success p { color: var(--muted-on-dark); margin-top: 10px; line-height: 1.6; }

        @media (max-width: 980px) { .hm-cta__grid { grid-template-columns: 1fr; } }
        @media (max-width: 600px) { .hm-cta__phones, .hm-cta__row { grid-template-columns: 1fr; } }
      `}</style>
    </section>
  );
}
